import React, { useState, useEffect, memo } from "react";
import ConfirmationModal from "../../reusable-components/modals/confirmation-modal/ConfirmationModal";
import LabelInputField from "../../core-components/label-input-field/LabelInputField";
import { BUTTON_COLOR } from "../../../types/coreButtonTypes";
import type { IPhotoLibrarySection } from "../../../interfaces/photoLibrary";
import ErrorText from "../../reusable-components/error-text/ErrorText";
import "./photoLibrarySections.scss";

interface PhotoLibraryConfirmationboxesProps {
	// Add New Section Modal
	showAddSectionModal: boolean;
	setShowAddSectionModal: (show: boolean) => void;
	onAddSection: (sectionName: string) => Promise<void>;
	sections: IPhotoLibrarySection[] | null;

	// Rename Section Modal
	renameSectionData: IPhotoLibrarySection | null;
	setRenameSectionData: (section: IPhotoLibrarySection | null) => void;
	onRenameSection: (sectionId: string, newName: string) => Promise<boolean>;
	activeSection: IPhotoLibrarySection | null;
	setActiveSection: (section: IPhotoLibrarySection | null) => void;

	// Delete Section Modal
	deleteSectionData: IPhotoLibrarySection | null;
	setDeleteSectionData: (section: IPhotoLibrarySection | null) => void;
	onDeleteSection: (section: IPhotoLibrarySection) => Promise<void>;
	setFirstSectionAsActive: () => void;
}

const PhotoLibraryConfirmationboxes: React.FC<PhotoLibraryConfirmationboxesProps> = ({
	showAddSectionModal,
	setShowAddSectionModal,
	onAddSection,
	sections,
	renameSectionData,
	setRenameSectionData,
	onRenameSection,
	activeSection,
	setActiveSection,
	deleteSectionData,
	setDeleteSectionData,
	onDeleteSection,
	setFirstSectionAsActive,
}) => {
	// Add Section Modal State
	const [newSectionName, setNewSectionName] = useState("");
	const [addSectionLoading, setAddSectionLoading] = useState(false);
	const [addSectionError, setAddSectionError] = useState("");

	// Rename Section Modal State
	const [renameSectionName, setRenameSectionName] = useState("");
	const [renameSectionLoading, setRenameSectionLoading] = useState(false);
	const [renameSectionError, setRenameSectionError] = useState("");

	// Reset add section modal when closed
	useEffect(() => {
		if (!showAddSectionModal) {
			setNewSectionName("");
			setAddSectionError("");
		}
	}, [showAddSectionModal]);

	useEffect(() => {
		if (!renameSectionData) {
			setRenameSectionName("");
			setRenameSectionError("");
		} else {
			setRenameSectionName(renameSectionData.sectionName || "");
		}
	}, [renameSectionData]);

	// Handle Add Section
	const handleAddSection = async () => {
		if (!newSectionName.trim()) {
			setAddSectionError("Section name is required");
			return;
		}

		if (sections) {
			if (sections.some((s) => s.sectionName.toLowerCase() === newSectionName.trim().toLowerCase())) {
				setAddSectionError("Section name already exists");
				return;
			}
		}

		setAddSectionLoading(true);
		setAddSectionError("");
		try {
			await onAddSection(newSectionName.trim());
			setShowAddSectionModal(false);
			setNewSectionName("");
		} catch (error) {
			setAddSectionError("Failed to add section. Please try again.");
		} finally {
			setAddSectionLoading(false);
		}
	};

	// Handle Rename Section
	const handleRenameSection = async () => {
		if (!renameSectionData) return;

		// Use current state value, fallback to renameSectionData.sectionName if state is empty
		const currentSectionName = renameSectionName.trim() || renameSectionData.sectionName || "";

		if (!currentSectionName) {
			setRenameSectionError("Section name is required");
			return;
		}

		if (currentSectionName === renameSectionData.sectionName) {
			setRenameSectionData(null);
			return;
		}

		if (
			sections &&
			sections?.some(
				(s) =>
					s.sectionName.toLowerCase() === currentSectionName.toLowerCase() && s.id !== renameSectionData.id,
			)
		) {
			setRenameSectionError("Section name already exists");
			return;
		}

		setRenameSectionLoading(true);
		setRenameSectionError("");
		try {
			const success = await onRenameSection(renameSectionData.id, currentSectionName);
			if (success) {
				// Update active section if it was renamed
				if (activeSection?.id === renameSectionData.id) {
					setActiveSection({ ...activeSection, sectionName: currentSectionName });
				}
				setRenameSectionData(null);
			} else {
				setRenameSectionError("Failed to rename section. Please try again.");
			}
		} catch (error) {
			setRenameSectionError("Failed to rename section. Please try again.");
		} finally {
			setRenameSectionLoading(false);
		}
	};

	// Handle Delete Section
	const handleDeleteSection = async () => {
		if (!deleteSectionData) return;
		await onDeleteSection(deleteSectionData);
		setDeleteSectionData(null);
		setFirstSectionAsActive();
	};

	return (
		<>
			{/* Add New Section Modal */}
			<ConfirmationModal
				open={showAddSectionModal}
				heading="Add New Section"
				description=""
				buttonText="Add Section"
				buttonColor={BUTTON_COLOR.PRIMARY}
				buttonLoading={addSectionLoading}
				onClose={() => setShowAddSectionModal(false)}
				onClickSave={handleAddSection}
				onClickCancel={() => setShowAddSectionModal(false)}
			>
				<LabelInputField
					className="add-section-name-input"
					label="Section Name"
					value={newSectionName}
					onChange={(e) => {
						setNewSectionName(e.target.value);
						setAddSectionError("");
					}}
					placeholder="Enter section name"
					error={!!addSectionError}
					id="new-section-name"
					required
					autoFocus
				/>
				{!!addSectionError && <ErrorText errorMessage={addSectionError} />}
			</ConfirmationModal>

			{/* Rename Section Modal */}
			<ConfirmationModal
				open={!!renameSectionData}
				heading="Rename Section"
				description=""
				buttonText="Rename"
				buttonColor={BUTTON_COLOR.PRIMARY}
				buttonLoading={renameSectionLoading}
				onClose={() => setRenameSectionData(null)}
				onClickSave={handleRenameSection}
				onClickCancel={() => setRenameSectionData(null)}
			>
				<LabelInputField
					className="rename-section-name-input"
					label="Section Name"
					value={renameSectionName}
					onChange={(e) => {
						setRenameSectionName(e.target.value);
						setRenameSectionError("");
					}}
					placeholder="Enter section name"
					error={!!renameSectionError}
					id="rename-section-name"
					required
					autoFocus
				/>
				{!!renameSectionError && <ErrorText errorMessage={renameSectionError} />}
			</ConfirmationModal>

			{/* Delete Section Modal */}
			<ConfirmationModal
				open={!!deleteSectionData}
				heading="Delete Section"
				description={`Are you sure you want to delete section "${deleteSectionData?.sectionName || ""}"? Photos in this section will be moved to Uncategorized.`}
				buttonText="Delete"
				buttonColor={BUTTON_COLOR.ERROR}
				onClose={() => setDeleteSectionData(null)}
				onClickSave={handleDeleteSection}
				onClickCancel={() => setDeleteSectionData(null)}
			/>
		</>
	);
};

export default memo(PhotoLibraryConfirmationboxes);
